"use client";

import * as React from "react";
import type { CarouselApi } from "@workspace/ui/components/carousel";
import { usePageVisibility } from "@/hooks/use-page-visibility";

export type StoryStatus = "idle" | "playing" | "paused";

export interface StoryState {
  status: StoryStatus;
  index: number;
  runId: number;
  /** The user navigated by hand: autoplay is stopped and the active pill shows no progress. */
  manual: boolean;
}

export type StoryAction =
  | { type: "enterView"; reducedMotion: boolean }
  | { type: "toggle" }
  | { type: "select"; index: number; manual: boolean }
  | { type: "restart" };

export const initialStoryState: StoryState = {
  status: "idle",
  index: 0,
  runId: 0,
  manual: false,
};

// Viewport, tab visibility, keyboard focus and buffering are not modelled here:
// they only gate `running`, so a user pause stays sticky across them.
export function storyReducer(
  state: StoryState,
  action: StoryAction,
): StoryState {
  switch (action.type) {
    case "enterView":
      if (state.status !== "idle") return state;
      return { ...state, status: action.reducedMotion ? "paused" : "playing" };

    case "toggle":
      if (state.status === "playing") return { ...state, status: "paused" };
      return {
        ...state,
        status: "playing",
        manual: false,
        // After a manual stop the pill was empty, so the slide's progress starts over.
        runId: state.manual ? state.runId + 1 : state.runId,
      };

    case "select":
      if (action.index === state.index) return state;
      return {
        ...state,
        index: action.index,
        runId: state.runId + 1,
        // Swipe, drag, dot or arrow key: stop autoplay until the user presses play.
        status: action.manual ? "paused" : state.status,
        manual: action.manual,
      };

    case "restart":
      return { ...state, runId: state.runId + 1 };

    default:
      return state;
  }
}

const REDUCED_MOTION_QUERY = "(prefers-reduced-motion: reduce)";
const VISIBILITY_THRESHOLDS = Array.from({ length: 21 }, (_, i) => i / 20);

// At least half the section is visible, or it fills at least half the viewport
// (a tall section on a short screen can never reach a 50% ratio).
function useMostlyInView(ref: React.RefObject<HTMLElement | null>) {
  const [inView, setInView] = React.useState(false);

  React.useEffect(() => {
    const element = ref.current;
    if (!element) return;

    if (typeof IntersectionObserver === "undefined") {
      setInView(true);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[entries.length - 1];
        if (!entry) return;
        const rootHeight = entry.rootBounds?.height ?? window.innerHeight;
        setInView(
          entry.isIntersecting &&
            (entry.intersectionRatio >= 0.5 ||
              entry.intersectionRect.height >= rootHeight * 0.5),
        );
      },
      { threshold: VISIBILITY_THRESHOLDS },
    );

    observer.observe(element);
    return () => observer.disconnect();
  }, [ref]);

  return inView;
}

function isFocusVisible(target: EventTarget) {
  try {
    return target instanceof Element && target.matches(":focus-visible");
  } catch {
    return false;
  }
}

interface UseStoryAutoplayOptions {
  api: CarouselApi | undefined;
  count: number;
  rootRef: React.RefObject<HTMLElement | null>;
  /** Temporarily hold the slider (e.g. a modal over the section), like scrolling out. */
  suspended?: boolean;
}

export interface UseStoryAutoplayResult {
  index: number;
  runId: number;
  status: StoryStatus;
  /** What the play/pause button shows: autoplay reads as paused while the section is scrolled out. */
  displayStatus: StoryStatus;
  manual: boolean;
  running: boolean;
  inView: boolean;
  toggle: () => void;
  goTo: (index: number) => void;
  onProgressEnd: () => void;
  setBuffering: (buffering: boolean) => void;
  focusHandlers: {
    onFocus: React.FocusEventHandler;
    onBlur: React.FocusEventHandler;
  };
}

export function useStoryAutoplay({
  api,
  count,
  rootRef,
  suspended = false,
}: UseStoryAutoplayOptions): UseStoryAutoplayResult {
  const [state, dispatch] = React.useReducer(storyReducer, initialStoryState);
  const inView = useMostlyInView(rootRef);
  const pageVisible = usePageVisibility();
  const [focusWithin, setFocusWithin] = React.useState(false);
  const [bufferingIndex, setBufferingIndex] = React.useState<number | null>(
    null,
  );

  const buffering = bufferingIndex === state.index;
  // Play pressed while the section is mostly scrolled out: run anyway until it next comes into view.
  const [viewportOverride, setViewportOverride] = React.useState(false);
  const visible = (inView || viewportOverride) && !suspended;

  React.useEffect(() => {
    if (inView) setViewportOverride(false);
  }, [inView]);
  // Set right before autoplay advances so the resulting `select` isn't treated as manual.
  const autoAdvancingRef = React.useRef(false);

  React.useEffect(() => {
    if (!inView || state.status !== "idle") return;
    // Read directly so a slow media-query hook can't race the first intersection.
    const reducedMotion = window.matchMedia(REDUCED_MOTION_QUERY).matches;
    dispatch({ type: "enterView", reducedMotion });
  }, [inView, state.status]);

  // Card videos restart from the beginning when they come back into view, so the active
  // slide's progress starts over too instead of resuming mid-way.
  const wasInViewRef = React.useRef(false);
  React.useEffect(() => {
    if (inView && !wasInViewRef.current && state.status === "playing") {
      dispatch({ type: "restart" });
    }
    wasInViewRef.current = inView;
  }, [inView, state.status]);

  React.useEffect(() => {
    if (!api) return;

    const onSelect = () => {
      const manual = !autoAdvancingRef.current;
      autoAdvancingRef.current = false;
      dispatch({ type: "select", index: api.selectedScrollSnap(), manual });
    };

    onSelect();
    api.on("select", onSelect);
    api.on("reInit", onSelect);

    return () => {
      api.off("select", onSelect);
      api.off("reInit", onSelect);
    };
  }, [api]);

  const toggle = React.useCallback(() => {
    if (state.status === "playing" && visible) {
      dispatch({ type: "toggle" });
      return;
    }
    // Shown as paused (by the user, or by scrolling out): pressing play must play.
    if (!inView) setViewportOverride(true);
    if (state.status !== "playing") dispatch({ type: "toggle" });
  }, [inView, state.status, visible]);

  const goTo = React.useCallback(
    (index: number) => {
      api?.scrollTo(index);
    },
    [api],
  );

  const onProgressEnd = React.useCallback(() => {
    if (state.status !== "playing") return;
    // Embla doesn't loop, so after the last slide autoplay rewinds to the first.
    if (count > 1 && api) {
      autoAdvancingRef.current = true;
      if (api.canScrollNext()) api.scrollNext();
      else api.scrollTo(0);
    } else {
      dispatch({ type: "restart" });
    }
  }, [api, count, state.status]);

  const setBuffering = React.useCallback(
    (value: boolean) => {
      setBufferingIndex(value ? state.index : null);
    },
    [state.index],
  );

  const focusHandlers = React.useMemo<UseStoryAutoplayResult["focusHandlers"]>(
    () => ({
      onFocus: (event) => {
        setFocusWithin(isFocusVisible(event.target));
      },
      onBlur: (event) => {
        const next = event.relatedTarget;
        if (!(next instanceof Node) || !event.currentTarget.contains(next)) {
          setFocusWithin(false);
        }
      },
    }),
    [],
  );

  const running =
    state.status === "playing" &&
    visible &&
    pageVisible &&
    !focusWithin &&
    !buffering;

  return {
    index: state.index,
    runId: state.runId,
    status: state.status,
    displayStatus:
      state.status === "playing" && !visible ? "paused" : state.status,
    manual: state.manual,
    running,
    inView,
    toggle,
    goTo,
    onProgressEnd,
    setBuffering,
    focusHandlers,
  };
}
